/**
 * Shop Home Page
 * Public page - no authentication required
 * Amazon-style homepage with featured products, categories, deals
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Main, Flex, Grid } from '../../../components/styled/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/styled/Card';
import { PrimaryButton } from '../../../components/styled/Button';
import productService from '../services/productService';
import useShopFeatures from '../hooks/useShopFeatures';
import { useCart } from '../contexts/CartContext';
import { getRatingValue, getRatingCount, renderStars, formatPrice } from '../utils/productHelpers';
import './Shop.css';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const features = useShopFeatures();
  const { addItem: addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    loadHomeData();
  }, []);

  const loadHomeData = async () => {
    try {
      setLoading(true);
      // Load featured products and categories
      const [products, cats] = await Promise.all([
        productService.getProducts({ limit: 8 }), // Removed featured filter - show all products
        productService.getCategories()
      ]);
      setFeaturedProducts(products.items || []);
      setCategories(cats || []);
    } catch (error) {
      console.error('Error loading home data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <Main>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        </Main>
      </Container>
    );
  }

  return (
    <Container>
      <Main>
        <div className="shop-home">
          {/* Hero Section */}
          <Card className="hero-banner">
            <CardContent>
              <h1>Welcome to Our Store</h1>
              <p>Discover amazing products at great prices</p>
              <PrimaryButton as={Link} to="/shop/catalog">
                Shop Now
              </PrimaryButton>
            </CardContent>
          </Card>

          {/* Categories */}
          <section className="categories-section">
            <h2>Shop by Category</h2>
            <Grid columns={4} gap="var(--space-4)">
              {categories.map(cat => (
                <Card key={cat.id} as={Link} to={`/shop/catalog?category=${cat.id}`} className="category-card">
                  <CardContent>
                    <h3>{cat.name}</h3>
                    <p>{cat.description}</p>
                  </CardContent>
                </Card>
              ))}
            </Grid>
          </section>

          {/* Featured Products */}
          <section className="featured-products">
            <h2>Featured Products</h2>
            {featuredProducts.length === 0 ? (
              <div className="empty-state">
                <h3>No products available</h3>
                <p>Check back soon for new products!</p>
              </div>
            ) : (
              <div className="product-grid">
                {featuredProducts.map(product => (
                  <div key={product.id} className="product-card-wrapper" onClick={() => navigate(`/shop/product/${product.id}`)}>
                    <Card className="product-card">
                      <div className="product-image-container">
                        <img src={product.image} alt={product.name} className="product-image" />
                      </div>
                      <CardContent>
                        <h3 className="product-name">{product.name}</h3>
                        {product.rating && (
                          <div className="product-rating">
                            <span className="stars">
                              {renderStars(product.rating)}
                            </span>
                            {getRatingCount(product.rating, product.ratingCount) > 0 && (
                              <span className="rating-count">({getRatingCount(product.rating, product.ratingCount)})</span>
                            )}
                          </div>
                        )}
                        <p className="price">${formatPrice(product.price)}</p>
                        {features.stockIndicators && product.stockQty !== undefined && (
                          <div className={`stock-badge ${product.stockQty > 0 ? 'in-stock' : 'out-of-stock'}`}>
                            {product.stockQty > 0 ? `${product.stockQty} in stock` : 'Out of stock'}
                          </div>
                        )}
                        <PrimaryButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            if (product.stockQty > 0) {
                              addToCart(product);
                              alert(`✅ ${product.name} added to cart!\n\nClick the 🛒 cart icon in the top-right corner to view your cart.`);
                            }
                          }}
                          disabled={product.stockQty === 0}
                          className="add-to-cart-btn"
                        >
                          {product.stockQty > 0 ? 'Add to Cart' : 'Out of Stock'}
                        </PrimaryButton>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </Main>
    </Container>
  );
};

export default Home;

